

import { IDENTITY_HUES } from '../config/brand';
import { BOOLEAN, CALENDAR, CALENDAR_CLOCK, CARD_ICON, CASH_COIN, CLOCK, DROPDOWN, EMAIL, HAND_THUMBS_UP, LIST_NESTED, LOCATION, MEASUREMENT, NUM_123, PALETTE, PAPERCLIP, PHONE, RATING_STAR, TEXT, TEXT_MULTI, TEXT_RICH, URL_ICON, USER_ACCOUNT } from '../components/icons';



/**
 * THE data type roster. Every type the card editor offers, in the order the
 * gallery shows them, each with:
 *
 *   name        the stored `element.dataType`
 *   label       what it is called everywhere in the UI
 *   family      the gallery group it belongs to (DATA_TYPE_FAMILIES)
 *   description one plain line: what a value of this type IS
 *   example     what such a value looks like, so the tile can show one
 *   synonyms    the words someone might search with ("dropdown" -> Choice)
 *   bsIcon(JSX) its glyph, from components/icons.js
 *
 * The picker (components/common/DataTypePicker.js), the badge
 * (DataTypeBadge.js), the multi add editor's ad hoc inputs and the list
 * filter vocabulary all read THIS list — there is no second type table.
 * KNOWN_TYPES in config/elementCoercion.js is the full set the API accepts
 * (these 22 plus the legacy `decimal` / `integer` spellings, which are not
 * offered to authors); getDataTypeMeta resolves those two to Number so an
 * element saved under an old spelling still reads as something.
 */
export const dataTypes = [
	{
		name: 'text-small',
		label: 'Text - short',
		family: 'Text',
		description: 'A short line of text.',
		example: 'Blue trim, second coat',
		synonyms: ['string', 'short', 'line', 'single line', 'name', 'title', 'plain'],
		bsIcon: 'TbTypography',
		bsIconJSX: <TEXT />,
	},
	{
		name: 'text-large',
		label: 'Text - long',
		family: 'Text',
		description: 'Several lines of plain text.',
		example: 'Sanded between coats; the north wall needed two.',
		synonyms: ['paragraph', 'multi line', 'multiline', 'textarea', 'long', 'notes', 'comments', 'description'],
		bsIcon: 'BsTextWrap',
		bsIconJSX: <TEXT_MULTI />,
	},
	{
		// Stored value is a plain markdown string; 'rich-text' drives the UI
		// (WYSIWYG editor with a raw-markdown mode) and markdown rendering on
		// display surfaces. Filtering/sorting treat it as text.
		name: 'rich-text',
		label: 'Rich text',
		family: 'Text',
		description: 'Formatted text with headings, lists and links.',
		example: '**Prep**, then two coats',
		synonyms: ['markdown', 'formatted', 'wysiwyg', 'editor', 'bold', 'heading', 'list', 'document'],
		bsIcon: 'BsFileRichtext',
		bsIconJSX: <TEXT_RICH />,
	},
	{
		// Stored item values are plain strings; 'choice' only drives the UI
		// (select input + choice-related options in the card definition form).
		name: 'choice',
		label: 'Choice',
		family: 'Choices',
		description: 'One value from a list you define.',
		example: 'In progress',
		synonyms: ['dropdown', 'select', 'picklist', 'options', 'list', 'status', 'category', 'enum', 'menu'],
		bsIcon: '',
		bsIconJSX: <DROPDOWN />,
	},
	{
		// Stored as a real boolean. New items start at false; a MISSING value
		// is "not set" (docs/plans/data-types.md decision 4). 'required' is
		// not offered — the element always has a value.
		name: 'boolean',
		label: 'Yes / no',
		family: 'Choices',
		description: 'Two states, in your own words.',
		example: 'Paid',
		synonyms: ['checkbox', 'toggle', 'switch', 'true', 'false', 'flag', 'yes', 'no', 'done'],
		bsIcon: 'BsToggleOn',
		bsIconJSX: <BOOLEAN />,
	},
	{
		name: 'rating',
		label: 'Rating',
		family: 'Choices',
		description: 'A score, out of five stars.',
		example: '4 of 5',
		synonyms: ['stars', 'score', 'review', 'rank', 'quality', 'grade'],
		bsIcon: '',
		bsIconJSX: <RATING_STAR />,
	},
	{
		// One lower-case "#rrggbb" (docs/plans/data-types-second-pass.md
		// §4.1). With the choices because it is picked from a palette —
		// decision D11; the owner may move it to a family of its own later,
		// which is one `family` here. Rendered as a swatch and the hex;
		// action: Copy. A new type sits in the gallery's order here, and is
		// APPENDED to KNOWN_TYPES (config/elementCoercion.js).
		name: 'color',
		label: 'Color',
		family: 'Choices',
		description: 'A color, picked from a swatch or typed as hex.',
		// The identity hue Blue, as stored; the gallery paints a swatch
		// before it (DataTypePicker).
		example: ( IDENTITY_HUES.find( hue => hue.key === 'blue' )?.value || '' ).toLowerCase(),
		synonyms: ['colour', 'hex', 'swatch', 'paint', 'shade', 'hue', 'tint', 'rgb'],
		bsIcon: 'BsPalette',
		bsIconJSX: <PALETTE />,
	},
	{
		name: 'number',
		label: 'Number',
		family: 'Numbers',
		description: 'A plain number, with or without decimals.',
		example: '24.5',
		synonyms: ['integer', 'decimal', 'amount', 'quantity', 'count', 'percent', 'total'],
		bsIcon: 'Bs123',
		bsIconJSX: <NUM_123 />,
	},
	{
		// A number WITH a unit: stored as { value, unit, base } where `base`
		// is server-set in the measure's canonical unit (docs/plans/
		// measurements.md). The element's options carry the measure, default
		// unit, allowlist and display rules; the shared utils/units module
		// formats it everywhere.
		name: 'measurement',
		label: 'Measurement',
		family: 'Numbers',
		description: 'A number with a unit, converted as needed.',
		example: '8 lb 7 oz',
		synonyms: ['unit', 'units', 'weight', 'mass', 'length', 'distance', 'volume', 'temperature', 'size', 'metric', 'imperial', 'rate', 'flow', 'per hour', 'per day', 'pulse', 'angle', 'degrees', 'frequency', 'rpm', 'hertz', 'fuel economy', 'mileage', 'mpg', 'torque', 'lug nut', 'ft-lb', 'N-m'],
		bsIcon: 'BsRulers',
		bsIconJSX: <MEASUREMENT />,
	},
	{
		// Money: stored as { amount, currency } -- `amount` an integer count
		// of MINOR units, `currency` an ISO 4217 code (docs/plans/
		// currency.md). The element's options carry the allowlist
		// (`currencies`), the default, bounds and the symbol/code style; the
		// shared utils/currency module parses and formats it everywhere.
		name: 'currency',
		label: 'Currency',
		family: 'Numbers',
		description: 'An amount of money in a currency you choose.',
		example: '$1,234.50',
		synonyms: ['money', 'price', 'cost', 'amount', 'dollars', 'euro', 'yen', 'budget', 'fee', 'payment', 'expense', 'spend', 'salary', 'cash'],
		bsIcon: 'BsCashCoin',
		bsIconJSX: <CASH_COIN />,
	},
	{
		// "Me too" (KPR-390; keepr-api utils/confirmations.js): how many people
		// said so — a whole number keepr keeps from who confirmed, absent until
		// the first and read as 0 everywhere. Nobody types it: the read view
		// draws a toggle (components/items/ConfirmationsValue.js) whose words
		// are the element's LABEL ("Me too", "+1"). With the numbers because it
		// reads, sorts, filters and charts as one; never a choice, though a
		// person picks it — what is stored is the count, not anyone's pick.
		// APPENDED to KNOWN_TYPES after `file` (config/elementCoercion.js).
		name: 'confirmations',
		label: 'Me too',
		family: 'Numbers',
		description: 'How many people said “me too”. Each person adds or takes back their own; only managers see who.',
		example: '12 people',
		synonyms: ['+1', 'upvote', 'vote', 'confirm', 'confirmations', 'same here', 'agree', 'me too', 'like', 'endorse', 'second'],
		bsIcon: 'BsHandThumbsUp',
		bsIconJSX: <HAND_THUMBS_UP />,
	},
	{
		name: 'date',
		label: 'Date',
		family: 'Dates & times',
		description: 'A calendar day, with no time of day.',
		example: 'Sep 13, 2026',
		synonyms: ['day', 'calendar', 'birthday', 'due', 'deadline', 'expires', 'anniversary', 'when'],
		bsIcon: 'BsCalendarDate',
		bsIconJSX: <CALENDAR />,
	},
	{
		name: 'date-time',
		label: 'Date & time',
		family: 'Dates & times',
		description: 'A day and a time of day.',
		example: 'Sep 13, 2026 3:15 PM',
		synonyms: ['timestamp', 'datetime', 'appointment', 'schedule', 'visit', 'moment', 'when'],
		bsIcon: '',
		bsIconJSX: <CALENDAR_CLOCK />,
	},
	{
		name: 'time',
		label: 'Time',
		family: 'Dates & times',
		description: 'A time of day, with no date.',
		example: '3:15 PM',
		synonyms: ['clock', 'hour', 'minute', 'oclock', 'time of day'],
		bsIcon: '',
		bsIconJSX: <CLOCK />,
	},
	{
		// Stored as E.164 ('+12025550123') when the number parses; the
		// element's `country` option (default US) is the region a national
		// number is read against, and the region a stored number reads back
		// in (docs/plans/data-types.md decision 6). Action: Call.
		name: 'phone',
		label: 'Phone',
		family: 'Contact & place',
		description: 'A telephone number, callable from the item.',
		example: '(202) 555-0123',
		synonyms: ['telephone', 'mobile', 'cell', 'call', 'contact', 'number', 'sms'],
		bsIcon: 'BsTelephone',
		bsIconJSX: <PHONE />,
	},
	{
		// Stored trimmed with the domain lower-cased; the local part is
		// case-sensitive by RFC and kept as typed (decision 7). Action:
		// Compose.
		name: 'email',
		label: 'Email',
		family: 'Contact & place',
		description: 'An email address, writable from the item.',
		example: 'sam@example.com',
		synonyms: ['mail', 'e-mail', 'address', 'contact', 'inbox', 'write'],
		bsIcon: 'BsEnvelope',
		bsIconJSX: <EMAIL />,
	},
	{
		name: 'url',
		label: 'URL',
		family: 'Contact & place',
		description: 'A web address, openable from the item.',
		example: 'https://example.com/manual',
		synonyms: ['link', 'web', 'website', 'address', 'internet', 'href', 'page'],
		bsIcon: '',
		bsIconJSX: <URL_ICON />,
		allowMultiple: true,
	},
	{
		// One element for an address, a pair of coordinates, or both:
		// `{ address?, lat?, lng? }` with only the keys that are set
		// (decision 5). The `accept` option narrows which halves it takes.
		// Filtering and sorting treat it as text over the address. Action:
		// Open in Maps.
		name: 'location',
		label: 'Location',
		family: 'Contact & place',
		description: 'An address, a pair of coordinates, or both.',
		example: '1600 Pennsylvania Ave NW',
		synonyms: ['address', 'map', 'place', 'gps', 'coordinates', 'latitude', 'longitude', 'city', 'where'],
		bsIcon: 'BsGeoAlt',
		bsIconJSX: <LOCATION />,
	},
	{
		name: 'card-lookup',
		label: 'Card lookup',
		family: 'Links',
		description: 'A link to an item of another card.',
		example: 'Sam Rivera (Person)',
		synonyms: ['link', 'reference', 'relation', 'relationship', 'related', 'lookup', 'person', 'owner', 'parent', 'join'],
		bsIcon: '',
		bsIconJSX: <CARD_ICON />,
	},
	{
		// A reference to a keepr ACCOUNT (docs/plans/element-sets-and-me.md):
		// the stored value is the user id string, or a sorted list of them
		// with `allowMultiple`. One user element per card may be the account
		// link (`options.identity`), which is what "me" resolves through.
		name: 'user',
		label: 'User',
		family: 'Links',
		description: 'A keepr account: someone who signs in.',
		example: 'Molly Byrne',
		synonyms: ['person', 'account', 'assignee', 'staff', 'member', 'owner', 'me'],
		bsIcon: 'BsPersonBadge',
		bsIconJSX: <USER_ACCOUNT />,
	},
	{
		// A file or photo bound to its own item (docs/plans/
		// data-types-file-element.md §2; the 10b contract): the stored value
		// is one attachment id, or a list of at most 20 under
		// `allowMultiple`. `accept` ('any' | 'image') and `maxSizeMb` (1–100)
		// are its rules. With the links because it points at a thing kept
		// elsewhere (the attachment), never at text. Rendered by
		// components/common/FileValue.js from the item read's `files` facts;
		// a file is not a record, so it never wears the record badge.
		// APPENDED to KNOWN_TYPES after `color` (config/elementCoercion.js).
		name: 'file',
		label: 'File',
		family: 'Links',
		description: 'A file or photo attached to this item.',
		example: 'receipt.pdf',
		synonyms: ['photo', 'picture', 'image', 'attachment', 'document', 'upload', 'pdf', 'scan', 'receipt', 'evidence'],
		bsIcon: 'BsPaperclip',
		bsIconJSX: <PAPERCLIP />,
	},
	{
		// A repeating group (KPR-679; keepr-api utils/repeatingGroups.js,
		// KPR-675; epic KPE-027): ONE element that holds an ordered list of
		// entries, each with the group's own columns. Its own family, 'Groups',
		// because it is not a value at all but a set of elements that repeat.
		// The author names it, adds its columns and sets the fewest and most
		// entries (docs/guides/data-types/repeating-group.md). APPENDED to
		// KNOWN_TYPES after `confirmations` (config/elementCoercion.js).
		name: 'repeating-group',
		label: 'Repeating group',
		family: 'Groups',
		description: 'A group of elements that repeat, once for every entry you add.',
		example: 'Ingredients in a recipe',
		synonyms: ['repeat', 'repeating', 'list', 'lines', 'line items', 'entries', 'table', 'steps', 'ingredients', 'checklist', 'rows', 'columns', 'subform', 'nested'],
		bsIcon: 'BsListNested',
		bsIconJSX: <LIST_NESTED />,
	},
];

/**
 * The gallery's groups, in order. Every dictionary entry names one of these
 * as its `family`; a type with a family missing from this list would never
 * be shown, so the pair is pinned by a test.
 */
export const DATA_TYPE_FAMILIES = ['Text', 'Choices', 'Numbers', 'Dates & times', 'Contact & place', 'Links', 'Groups'];

// The legacy numeric spellings the API still accepts but no author picks:
// they read as Number wherever a type is named.
const LEGACY_TYPE_ALIASES = { decimal: 'number', integer: 'number' };

/**
 * getDataTypeMeta(name) -> the dictionary entry for a stored dataType, or
 * null when there is none. The legacy numeric spellings resolve to Number;
 * an unset or unknown type is null, which is what the badge and the picker
 * render as "Choose a type".
 */
export const getDataTypeMeta = ( name ) => {
	if ( ! name || typeof name !== 'string' ) return null;
	const resolved = LEGACY_TYPE_ALIASES[ name ] || name;
	return dataTypes.find( entry => entry.name === resolved ) || null;
};

/** The words a type answers to in the picker's search box, as one string. */
export const dataTypeSearchText = ( entry ) => [
	entry?.label,
	entry?.description,
	entry?.name,
	...( entry?.synonyms || [] ),
].filter( Boolean ).join( ' ' );

/** The dictionary as grouped antd Select options: one group per family. */
export const dataTypeOptionGroups = () => DATA_TYPE_FAMILIES
	.map( family => ( {
		label: family,
		options: dataTypes
			.filter( type => type.family === family )
			.map( type => ( {
				value: type.name,
				label: type.label,
				description: type.description,
				icon: type.bsIconJSX,
				searchText: dataTypeSearchText( type ),
			} ) ),
	} ) )
	.filter( group => group.options.length );

export const getDictionaryFromName = ( name ) => {
	return dataTypes.find( ele => ele.name === name );
}



export const elementColors = [
	{ value: 'Olive', label: 'Olive' },
	{ value: 'ForestGreen', label: 'Forest green' },
	{ value: 'LimeGreen', label: 'Lime green' },
	{ value: 'PaleGreen', label: 'Pale green' },
	{ value: 'Gold', label: 'Gold' },
	{ value: 'DarkOrange', label: 'Orange' },
	{ value: 'Tomato', label: 'Tomato' },
	{ value: 'Crimson', label: 'Crimson' },
	{ value: 'DeepPink', label: 'Pink' },
	{ value: 'Violet', label: 'Violet' },
	{ value: 'DarkMagenta', label: 'Purple' },
	{ value: 'PaleTurquoise', label: 'Light blue' },
	{ value: 'DodgerBlue', label: 'Blue' },
	{ value: 'DarkCyan', label: 'Dark cyan' },
	{ value: 'Aqua', label: 'Aqua' },
	{ value: 'Wheat', label: 'Wheat' },
	{ value: 'Tan', label: 'Tan' },
	{ value: 'SandyBrown', label: 'Sandy brown' },
	{ value: 'Chocolate', label: 'Light brown' },
	{ value: 'SaddleBrown', label: 'Dark brown' },
	{ value: 'LightGray', label: 'Light gray' },
	{ value: 'DarkGray', label: 'Dark gray' },
];




